import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, TextField } from "@core/components";
import { Icon } from "@core/assets";
import { DossierField } from "./DossierField";
import {
  teamCapitalDistributionOptions,
  teamTimeCommitmentOptions,
} from "./team-catalog-mirror";
import type { MemberMutationResult } from "../hooks";
import type { TeamMember } from "../types";

const MAX_MEMBERS = 5;

/**
 * One saved member — every field here is a real edit (autosave-on-commit, same shape as any other
 * field). Time commitment and capital distribution round-trip through a temporary hand-mirrored
 * option list (`team-catalog-mirror.ts`) rather than the round's own catalogue — see that file's
 * doc comment for why, and delete it there first if this comment and that file ever disagree.
 */
function MemberRow({
  member,
  isDisabled,
  fieldErrors,
  onUpdateMember,
  onDeleteMember,
}: {
  member: TeamMember;
  isDisabled: boolean;
  /** Already sliced to THIS member's `member:{id}:{field}` keys — see `TeamMembersPanel`. */
  fieldErrors: Record<string, string>;
  onUpdateMember: (memberId: string, values: Record<string, unknown>) => void;
  onDeleteMember: (memberId: string) => Promise<MemberMutationResult>;
}) {
  const { t, i18n } = useTranslation("acquire");
  const [isRemoving, setIsRemoving] = useState(false);

  /** Uncommitted edits, layered over the saved member — the same pattern `DossierSectionStep` uses. */
  const [drafts, setDrafts] = useState<Record<string, unknown>>({});
  const valueOf = (key: keyof TeamMember): unknown =>
    key in drafts ? drafts[key] : member[key];
  // Takes the value directly rather than re-reading `drafts` — see `DossierField`'s `onCommit`
  // doc comment for why re-deriving it here would send a stale, one-click-behind value.
  const commit = (key: string, value: unknown) => {
    if (value === undefined) return;
    onUpdateMember(member.id, { [key]: value });
  };

  const handleRemove = async () => {
    setIsRemoving(true);
    const result = await onDeleteMember(member.id);
    // On success the row is gone from the next render (the roster re-reads `application.team.members`
    // wholesale); on failure it is still here, so only the failure path needs to reset the spinner.
    if (!result.ok) setIsRemoving(false);
  };

  return (
    <li className="flex flex-col gap-md rounded-lg border border-(--border-default) p-md">
      <div className="flex items-start justify-between gap-sm">
        <p className="text-sm-semibold text-(--text-primary)">
          {member.name || t("dossier.team.member.unnamed")}
        </p>
        <button
          type="button"
          disabled={isDisabled || isRemoving}
          onClick={() => void handleRemove()}
          aria-label={t("dossier.team.member.remove-label", {
            name: member.name,
          })}
          className="shrink-0 rounded-sm p-1 text-(--text-tertiary) outline-none hover:text-(--text-error) focus-visible:ring-2 focus-visible:ring-focus-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Icon name="trash" size={18} />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
        <DossierField
          label={t("dossier.team.member.name")}
          helpText=""
          inputType="SHORT_TEXT"
          isRequired
          options={[]}
          value={valueOf("name")}
          onChange={(next) =>
            setDrafts((current) => ({ ...current, name: next }))
          }
          onCommit={(value) => commit("name", value)}
          isDisabled={isDisabled}
          errorMessage={fieldErrors.name}
        />
        <DossierField
          label={t("dossier.team.member.role")}
          helpText=""
          inputType="SHORT_TEXT"
          isRequired
          options={[]}
          value={valueOf("role")}
          onChange={(next) =>
            setDrafts((current) => ({ ...current, role: next }))
          }
          onCommit={(value) => commit("role", value)}
          isDisabled={isDisabled}
          errorMessage={fieldErrors.role}
        />
        <DossierField
          label={t("dossier.team.member.capital-percentage")}
          helpText=""
          inputType="NUMBER"
          // The backend stores this to 2 decimal places (a DecimalField) — without this, the
          // shared NumberField's whole-number default silently turned "12.5" into "125" on save.
          maxFractionDigits={2}
          isRequired={false}
          options={[]}
          value={valueOf("capital_percentage")}
          onChange={(next) =>
            setDrafts((current) => ({ ...current, capital_percentage: next }))
          }
          onCommit={(value) => commit("capital_percentage", value)}
          isDisabled={isDisabled}
          errorMessage={fieldErrors.capital_percentage}
        />
      </div>

      <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
        <DossierField
          label={t("dossier.team.member.time-commitment")}
          helpText=""
          inputType="DROPDOWN"
          isRequired={false}
          options={teamTimeCommitmentOptions(i18n.language)}
          value={valueOf("time_commitment_code")}
          onChange={(next) =>
            setDrafts((current) => ({ ...current, time_commitment_code: next }))
          }
          onCommit={(value) => commit("time_commitment_code", value)}
          isDisabled={isDisabled}
          errorMessage={fieldErrors.time_commitment_code}
        />
        <DossierField
          label={t("dossier.team.member.capital-distribution")}
          helpText=""
          inputType="DROPDOWN"
          isRequired={false}
          options={teamCapitalDistributionOptions(i18n.language)}
          value={valueOf("capital_distribution_code")}
          onChange={(next) =>
            setDrafts((current) => ({
              ...current,
              capital_distribution_code: next,
            }))
          }
          onCommit={(value) => commit("capital_distribution_code", value)}
          isDisabled={isDisabled}
          errorMessage={fieldErrors.capital_distribution_code}
        />
      </div>
    </li>
  );
}

/**
 * « Membres de l'équipe » — the add/edit/remove roster shown only while `mode === "MULTIPLE"`.
 *
 * ## Why this exists outside the ordinary field list
 *
 * Every other applicant control in this dossier is a `SchemaField` — configuration the round
 * authored, rendered generically. A team member is not: it is a real sub-resource
 * (`ApplicationTeamMember`) with its own `POST|PATCH|DELETE /team/members/{id}/` endpoints, so it
 * needs real create/remove actions rather than a value to type into an existing row.
 *
 * ## Create waits for a deliberate confirm, edits autosave
 *
 * Matches the round builder's own rule for exactly this shape of problem (`rules/rounds.md`,
 * "Nothing writes until the author says so"): a bare "Add" that POSTed immediately would need to
 * invent a placeholder name to make the request valid, and a half-typed name would reach the
 * server one keystroke at a time. So adding opens a small local draft (name + role) and only POSTs
 * once both are filled and confirmed; every field on an EXISTING member still autosaves on blur,
 * same as every other field in this dossier — the member already exists, there is nothing left to
 * protect it from.
 *
 * ## Two fields read their options from a temporary hand mirror, not the API
 *
 * `time_commitment_code` and `capital_distribution_code` are catalogue-backed, but the applicant
 * API never hands the applicant their option labels — every OTHER catalogue field on this dossier
 * gets its options embedded straight in `schema.applicant.sections[].fields[].options`; these two
 * simply never got the same treatment, and the one endpoint that lists them by category
 * (`GET /intake-rounds/{id}/reference-catalog/`) is administrator/coordinator-only. Guessing the
 * codes IS the exact `EDUCATION_LEVEL_CODES` mistake `rules/rounds.md` documents — done anyway
 * here, deliberately and with the tradeoff flagged (Akram, 2026-08-28), to unblock the UI while
 * `DZCH-50` asks Aymen for the real labels. See `team-catalog-mirror.ts` for the full reasoning,
 * the source of the values, and what to delete once the backend ships them.
 */
export function TeamMembersPanel({
  members,
  isDisabled,
  fieldErrors,
  onCreateMember,
  onUpdateMember,
  onDeleteMember,
}: {
  members: TeamMember[];
  isDisabled: boolean;
  /** The FULL shared map — this panel picks out its own `member:{id}:{field}` slice per row. */
  fieldErrors: Record<string, string>;
  onCreateMember: (values: {
    name: string;
    role: string;
  }) => Promise<MemberMutationResult>;
  onUpdateMember: (memberId: string, values: Record<string, unknown>) => void;
  onDeleteMember: (memberId: string) => Promise<MemberMutationResult>;
}) {
  const { t } = useTranslation("acquire");

  const [isAdding, setIsAdding] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftRole, setDraftRole] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createErrors, setCreateErrors] = useState<{
    name?: string;
    role?: string;
  }>({});

  const atLimit = members.length >= MAX_MEMBERS;

  const closeDraft = () => {
    setIsAdding(false);
    setDraftName("");
    setDraftRole("");
    setCreateErrors({});
  };

  const submitDraft = async () => {
    setIsSubmitting(true);
    const result = await onCreateMember({
      name: draftName.trim(),
      role: draftRole.trim(),
    });
    setIsSubmitting(false);
    if (result.ok) {
      closeDraft();
    } else {
      setCreateErrors({
        name: result.fieldErrors.name,
        role: result.fieldErrors.role,
      });
    }
  };

  const memberErrors = (memberId: string): Record<string, string> => {
    const prefix = `member:${memberId}:`;
    const scoped: Record<string, string> = {};
    for (const [key, message] of Object.entries(fieldErrors)) {
      if (key.startsWith(prefix)) scoped[key.slice(prefix.length)] = message;
    }
    return scoped;
  };

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-center justify-between gap-sm">
        <h2 className="text-md-semibold text-(--text-primary)">
          {t("dossier.team.members-title")}
        </h2>
        <p className="shrink-0 text-xs-medium text-(--text-tertiary)">
          {t("dossier.team.members-count", {
            count: members.length,
            max: MAX_MEMBERS,
          })}
        </p>
      </div>

      {members.length === 0 && !isAdding ? (
        <p className="text-sm-regular text-(--text-tertiary)">
          {t("dossier.team.members-empty")}
        </p>
      ) : (
        <ul className="flex flex-col gap-md">
          {members.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              isDisabled={isDisabled}
              fieldErrors={memberErrors(member.id)}
              onUpdateMember={onUpdateMember}
              onDeleteMember={onDeleteMember}
            />
          ))}
        </ul>
      )}

      {isAdding ? (
        <div className="flex flex-col gap-md rounded-lg border border-dashed border-(--border-default) p-md">
          <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
            <TextField
              label={t("dossier.team.member.name")}
              isRequired
              value={draftName}
              onChange={setDraftName}
              isDisabled={isSubmitting}
              errorMessage={createErrors.name}
            />
            <TextField
              label={t("dossier.team.member.role")}
              isRequired
              value={draftRole}
              onChange={setDraftRole}
              isDisabled={isSubmitting}
              errorMessage={createErrors.role}
            />
          </div>
          <div className="flex justify-end gap-sm">
            <Button
              variant="secondary"
              isDisabled={isSubmitting}
              onPress={closeDraft}
            >
              {t("dossier.team.member.cancel")}
            </Button>
            <Button
              variant="primary"
              isPending={isSubmitting}
              isDisabled={
                isSubmitting ||
                draftName.trim() === "" ||
                draftRole.trim() === ""
              }
              onPress={() => void submitDraft()}
            >
              {t("dossier.team.member.confirm-add")}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="secondary"
          isDisabled={isDisabled || atLimit}
          onPress={() => setIsAdding(true)}
        >
          <Icon name="plus" size={16} />
          {t("dossier.team.member.add")}
        </Button>
      )}

      {atLimit && !isAdding ? (
        <p className="text-xs-regular text-(--text-tertiary)">
          {t("dossier.team.members-limit")}
        </p>
      ) : null}
    </div>
  );
}
